/**
 * Single source of truth for the header shown on every generated report PDF
 * (Minister's, Finance & Statistics, and the long-table department report).
 * Consumed by the jsPDF exporters via reportPdfCommon so wording/order match.
 */

export const NTCOG_ORG_NAME = "New Testament Church of God";
export const NTCOG_ORG_REGION = "Jamaica";

export type ReportPrintScope = "church" | "district" | "nation";

/** Loose shape shared by PastorMeta / FinanceMeta / long-table meta. */
export type PrintHeaderMeta = {
  church_name?: string | null;
  district_name?: string | null;
  nation_name?: string | null;
  department_name?: string | null;
  report_tag_name?: string | null;
  report_tag_since?: string | null;
  report_tag_until?: string | null;
};

export type PrintHeaderFields = {
  organization: string;
  region: string;
  entityLabel: string; // "Church" | "District" | "Nation" | "Entity"
  entityName: string;
  departmentName: string;
  period: string;
  rangeSince: string | null;
  rangeUntil: string | null;
};

function clean(v?: string | null): string {
  return (v ?? "").trim();
}

export function buildPrintHeaderFields(
  meta: PrintHeaderMeta | null | undefined,
  scope?: ReportPrintScope,
  overrides?: { entityName?: string }
): PrintHeaderFields {
  const church = clean(meta?.church_name);
  const district = clean(meta?.district_name);
  const nation = clean(meta?.nation_name);

  let entityLabel: string;
  let entityName = clean(overrides?.entityName);

  if (scope === "district") {
    entityLabel = "District";
    entityName = entityName || district;
  } else if (scope === "nation") {
    entityLabel = "Nation";
    entityName = entityName || nation;
  } else if (scope === "church") {
    entityLabel = "Church";
    entityName = entityName || church;
  } else if (church) {
    entityLabel = "Church";
    entityName = entityName || church;
  } else if (district) {
    entityLabel = "District";
    entityName = entityName || district;
  } else if (nation) {
    entityLabel = "Nation";
    entityName = entityName || nation;
  } else {
    entityLabel = "Entity";
  }

  return {
    organization: NTCOG_ORG_NAME,
    region: NTCOG_ORG_REGION,
    entityLabel,
    entityName: entityName || "—",
    departmentName: clean(meta?.department_name) || "—",
    period: clean(meta?.report_tag_name) || "—",
    rangeSince: meta?.report_tag_since ?? null,
    rangeUntil: meta?.report_tag_until ?? null,
  };
}

/** Build the `Entity - Report - Period` file base (no extension), safe for filenames. */
export function buildReportFileBase(fields: PrintHeaderFields, reportTitle?: string): string {
  const report = clean(reportTitle) || fields.departmentName;
  const parts = [fields.entityName, report, fields.period].filter((p) => p && p !== "—");
  const base = parts.join(" - ") || "Report";
  return base.replace(/[^a-zA-Z0-9 \-_.]/g, "").replace(/\s+/g, " ").trim();
}
